/**
 * Database Storage Service
 *
 * Persists databases (and their items) to localStorage.
 * Same pattern as pageStorage.ts.
 *
 * Storage key: `freshcards:databases`
 * Format: JSON-serialised map of databaseId → Database
 */

import type { Database, DatabaseItem } from '../types/models/Database';
import type { PropertyDefinition } from '../types/models/Property';

const STORAGE_KEY = 'freshcards:databases';

type DatabaseMap = Record<string, Database>;

/**
 * Load all databases from storage.
 * Returns an empty object if nothing is stored or the stored data is corrupt.
 */
export function loadDatabases(): DatabaseMap {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return {};
    const parsed = JSON.parse(data);
    if (typeof parsed !== 'object' || parsed === null) return {};
    // Re-hydrate Date fields (JSON.parse turns them into strings)
    for (const db of Object.values(parsed) as Database[]) {
      db.createdAt = new Date(db.createdAt);
      db.updatedAt = new Date(db.updatedAt);
      for (const item of db.items) {
        item.createdAt = new Date(item.createdAt);
        item.updatedAt = new Date(item.updatedAt);
      }
    }
    return parsed;
  } catch (error) {
    console.error('[DatabaseStorage] Failed to load databases:', error);
    return {};
  }
}

/**
 * Save all databases to storage. Replaces the entire map.
 */
export function saveDatabases(databases: DatabaseMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(databases));
  } catch (error) {
    console.error('[DatabaseStorage] Failed to save databases:', error);
  }
}

/**
 * Load a single database by id.
 * Returns undefined if not found.
 */
export function loadDatabase(databaseId: string): Database | undefined {
  const all = loadDatabases();
  return all[databaseId];
}

/**
 * Save a single database. Other databases are preserved.
 */
export function saveDatabase(database: Database): void {
  const all = loadDatabases();
  all[database.id] = { ...database, updatedAt: new Date() };
  saveDatabases(all);
}

/**
 * Delete a database by id.
 */
export function deleteDatabase(databaseId: string): void {
  const all = loadDatabases();
  delete all[databaseId];
  saveDatabases(all);
}

/**
 * Create a fresh database with sensible defaults:
 * - Title property
 * - Date property (for date grouping)
 * - Status property (Active / Completed)
 * - An empty items list
 *
 * Useful for the "New Database" quick action.
 */
export function createDefaultDatabase(id: string, name: string = 'Untitled Database'): Database {
  const now = new Date();
  const titleProp: PropertyDefinition = {
    id: 'title',
    name: 'Title',
    type: 'title',
    visible: true,
    position: 0,
  };
  const dateProp: PropertyDefinition = {
    id: 'date',
    name: 'Date',
    type: 'date',
    visible: true,
    position: 1,
    dateFormat: 'YYYY-MM-DD',
  };
  const statusProp: PropertyDefinition = {
    id: 'status',
    name: 'Status',
    type: 'status',
    visible: true,
    position: 2,
    statusOptions: [
      { id: 'status_active', name: 'Active', color: '#52b788', state: 'active' },
      { id: 'status_completed', name: 'Completed', color: '#adb5bd', state: 'completed' },
    ],
  };
  const tagProp: PropertyDefinition = {
    id: 'tag',
    name: 'Tag',
    type: 'multi_select',
    visible: true,
    position: 3,
    options: [
      { id: 'tag_freshvibe', name: 'FRESHVIBE', color: '#c4b5fd' },
    ],
  };
  const checkboxProp: PropertyDefinition = {
    id: 'finished',
    name: 'Finished',
    type: 'checkbox',
    visible: true,
    position: 4,
  };
  return {
    id,
    name,
    schema: {
      id: `${id}_schema`,
      properties: [titleProp, dateProp, statusProp, tagProp, checkboxProp],
    },
    views: [
      { id: `${id}_kanban`, name: 'Board', type: 'kanban' },
      { id: `${id}_list`, name: 'List', type: 'list' },
    ],
    items: [],
    createdAt: now,
    updatedAt: now,
  };
}

export default {
  loadDatabases,
  saveDatabases,
  loadDatabase,
  saveDatabase,
  deleteDatabase,
  createDefaultDatabase,
};
